import { JSX, useEffect, useMemo, useRef, useState } from 'react';
import { FieldPath, UseFormReturn } from 'react-hook-form';

import { RHFCustomerSelect } from 'components/input/RHFCustomerSelect';
import { RHFLocationSelect } from 'components/input/RHFLocationSelect';
import { TMSCustomer, TMSLocation } from 'types/Load';
import { Maybe, Undef } from 'types/UtilityTypes';
import { injectSelectedObject } from 'utils/loadInfoAndBuilding';

import { PLS_SECTION_LABELS } from './constants';
import { PlsFormValues } from './types';
import {
  PlsBillToTransitionAction,
  PlsLocationTransitionAction,
  createEmptyPlsBillTo,
  getAutomaticPlsBillTo,
  getAutomaticPlsLocationId,
  getPlsBillToTransition,
  getPlsLocationTransition,
  getPlsRawLocationId,
  plsLocationsBillToFirst,
  plsLocationsForBillTo,
} from './utils';

export type CustomerSectionFormProps = {
  formMethods: UseFormReturn<PlsFormValues>;
  tmsID?: number;
  customers: Maybe<TMSCustomer[]>;
  setCustomers: React.Dispatch<React.SetStateAction<Maybe<TMSCustomer[]>>>;
  customerLocations: Maybe<TMSLocation[]>;
  setCustomerLocations: React.Dispatch<
    React.SetStateAction<Maybe<TMSLocation[]>>
  >;
  customerLocationCatalog: Maybe<TMSLocation[]>;
  locationCatalogCustomerId: Undef<string>;
  isLoadingCustomers: boolean;
  isLoadingCustomerLocations: boolean;
  setIsLoadingCustomers: React.Dispatch<React.SetStateAction<boolean>>;
  refreshCustomerLocations: () => void;
  suggestedCustomerExternalTMSID?: string;
  suggestedBillToExternalTMSID?: string;
  autoBillToId: Undef<string>;
  setAutoBillToId: React.Dispatch<React.SetStateAction<Undef<string>>>;
  isCreateMode?: boolean;
};

export function CustomerSectionForm({
  formMethods,
  tmsID,
  customers,
  setCustomers,
  customerLocations,
  setCustomerLocations,
  customerLocationCatalog,
  locationCatalogCustomerId,
  isLoadingCustomers,
  isLoadingCustomerLocations,
  setIsLoadingCustomers,
  refreshCustomerLocations,
  suggestedCustomerExternalTMSID,
  suggestedBillToExternalTMSID,
  autoBillToId,
  setAutoBillToId,
  isCreateMode = true,
}: CustomerSectionFormProps): JSX.Element {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = formMethods;

  const watchedCustomerId = watch('customer.externalTMSID') ?? '';
  const watchedBillToId = watch('billTo.externalTMSID') ?? '';
  const watchedSelectedLocationId = watch('plsSelectedLocationId') ?? '';
  const lastSyncedCustomerIdRef = useRef<Undef<string>>(undefined);
  const [autoLocationId, setAutoLocationId] = useState<Undef<string>>();
  const [hasManualLocationOverride, setHasManualLocationOverride] =
    useState(false);
  const [hasManualBillToOverride, setHasManualBillToOverride] = useState(false);
  // A suggested bill-to (from the customer's booking history) lists its locations first. Every
  // location stays listed, so a rep can still pick one under another bill-to.
  const suggestedBillToForLocations =
    suggestedCustomerExternalTMSID === watchedCustomerId &&
    suggestedBillToExternalTMSID === watchedBillToId
      ? suggestedBillToExternalTMSID
      : undefined;
  const customerLocationSelectOptions = useMemo(
    () =>
      customerLocations
        ? plsLocationsBillToFirst(
            customerLocations,
            suggestedBillToForLocations
          ).map((location) => ({
            ...location,
            externalTMSID: getPlsRawLocationId(location.externalTMSID),
          }))
        : customerLocations,
    [customerLocations, suggestedBillToForLocations]
  );

  // PLS books a location under one bill-to, so picking a location under another one switches the
  // bill-to to it. The rep chose it, so it counts as a manual bill-to.
  const handleLocationSelected = (location: TMSLocation) => {
    const locationBillTo = getAutomaticPlsBillTo(
      [location],
      location.externalTMSID
    );
    if (
      !locationBillTo ||
      locationBillTo.externalTMSID ===
        formMethods.getValues('billTo.externalTMSID')
    ) {
      return;
    }
    setValue('billTo', locationBillTo, { shouldDirty: true });
    setValue('plsOperationalFields', {}, { shouldDirty: true });
    setAutoBillToId(undefined);
    setHasManualBillToOverride(true);
  };

  useEffect(() => {
    if (
      !watchedCustomerId ||
      customers?.some(
        (customer) =>
          String(customer.externalTMSID) === String(watchedCustomerId)
      )
    ) {
      return;
    }

    const selectedCustomer = formMethods.getValues('customer') as TMSCustomer;
    if (!selectedCustomer.name) {
      return;
    }

    setCustomers((currentCustomers) =>
      injectSelectedObject(selectedCustomer, currentCustomers ?? [])
    );
  }, [customers, formMethods, setCustomers, watchedCustomerId]);

  useEffect(() => {
    const externalTMSID = watchedCustomerId;
    if (!externalTMSID) {
      if (lastSyncedCustomerIdRef.current) {
        setValue('plsSelectedLocationId', '', { shouldDirty: true });
        setValue('billTo', createEmptyPlsBillTo(), { shouldDirty: true });
        setValue('plsOperationalFields', {}, { shouldDirty: true });
      }
      lastSyncedCustomerIdRef.current = undefined;
      setAutoBillToId(undefined);
      setAutoLocationId(undefined);
      setHasManualLocationOverride(false);
      setHasManualBillToOverride(false);
      return;
    }

    const selectedCustomer = customers?.find(
      (customer) => customer.externalTMSID === externalTMSID
    );
    if (!selectedCustomer) {
      return;
    }

    const previousCustomerId = lastSyncedCustomerIdRef.current;
    if (previousCustomerId === externalTMSID) {
      return;
    }

    Object.entries(selectedCustomer).forEach(([key, value]) => {
      setValue(`customer.${key}` as FieldPath<PlsFormValues>, value, {
        shouldDirty: true,
      });
    });

    if (previousCustomerId) {
      setValue('plsSelectedLocationId', '', { shouldDirty: true });
      const hasActiveSuggestedBillTo =
        suggestedCustomerExternalTMSID === externalTMSID &&
        suggestedBillToExternalTMSID === watchedBillToId;
      if (!hasActiveSuggestedBillTo) {
        setValue('billTo', createEmptyPlsBillTo(), { shouldDirty: true });
      }
      setValue('plsOperationalFields', {}, { shouldDirty: true });
      setAutoBillToId(undefined);
      setAutoLocationId(undefined);
      setHasManualLocationOverride(false);
      setHasManualBillToOverride(false);
    }

    lastSyncedCustomerIdRef.current = externalTMSID;
  }, [
    watchedCustomerId,
    watchedBillToId,
    customers,
    setValue,
    suggestedCustomerExternalTMSID,
    suggestedBillToExternalTMSID,
    setAutoBillToId,
  ]);

  useEffect(() => {
    if (
      !isCreateMode ||
      !watchedCustomerId ||
      isLoadingCustomerLocations ||
      locationCatalogCustomerId !== watchedCustomerId ||
      hasManualLocationOverride
    ) {
      return;
    }

    const currentLocationId =
      formMethods.getValues('plsSelectedLocationId') ?? '';
    const automaticLocationId = getAutomaticPlsLocationId(
      plsLocationsForBillTo(
        customerLocationCatalog,
        suggestedBillToForLocations
      )
    );
    const transition = getPlsLocationTransition(
      currentLocationId,
      autoLocationId,
      automaticLocationId
    );

    if (transition.action === PlsLocationTransitionAction.Clear) {
      setValue('plsSelectedLocationId', '', { shouldDirty: true });
      setAutoLocationId(undefined);
      return;
    }

    if (
      transition.action === PlsLocationTransitionAction.Replace &&
      automaticLocationId
    ) {
      setValue('plsSelectedLocationId', automaticLocationId, {
        shouldDirty: true,
      });
      setAutoLocationId(transition.nextAutoLocationId);
    }
  }, [
    isCreateMode,
    watchedCustomerId,
    isLoadingCustomerLocations,
    locationCatalogCustomerId,
    customerLocationCatalog,
    suggestedBillToForLocations,
    hasManualLocationOverride,
    autoLocationId,
    formMethods,
    setValue,
  ]);

  useEffect(() => {
    if (autoLocationId && watchedSelectedLocationId !== autoLocationId) {
      setAutoLocationId(undefined);
      setHasManualLocationOverride(true);
    }
  }, [watchedSelectedLocationId, autoLocationId]);

  useEffect(() => {
    if (
      !isCreateMode ||
      isLoadingCustomerLocations ||
      locationCatalogCustomerId !== watchedCustomerId ||
      hasManualBillToOverride
    ) {
      return;
    }

    const currentBillToId = formMethods.getValues('billTo.externalTMSID') ?? '';
    const automaticBillTo = getAutomaticPlsBillTo(
      customerLocationCatalog,
      watchedSelectedLocationId
    );
    const activeSuggestedBillToId =
      suggestedCustomerExternalTMSID === watchedCustomerId
        ? suggestedBillToExternalTMSID
        : undefined;
    const transition = getPlsBillToTransition(
      currentBillToId,
      autoBillToId,
      automaticBillTo?.externalTMSID,
      activeSuggestedBillToId
    );

    if (transition.action === PlsBillToTransitionAction.Clear) {
      setValue('billTo', createEmptyPlsBillTo(), { shouldDirty: true });
      setValue('plsOperationalFields', {}, { shouldDirty: true });
      setAutoBillToId(undefined);
      return;
    }

    if (
      transition.action === PlsBillToTransitionAction.Replace &&
      automaticBillTo
    ) {
      setValue('billTo', automaticBillTo, { shouldDirty: true });
      setValue('plsOperationalFields', {}, { shouldDirty: true });
      setAutoBillToId(transition.nextAutoBillToId);
    }
  }, [
    isCreateMode,
    watchedCustomerId,
    watchedSelectedLocationId,
    isLoadingCustomerLocations,
    locationCatalogCustomerId,
    customerLocationCatalog,
    suggestedCustomerExternalTMSID,
    suggestedBillToExternalTMSID,
    hasManualBillToOverride,
    autoBillToId,
    formMethods,
    setValue,
    setAutoBillToId,
  ]);

  useEffect(() => {
    if (autoBillToId && watchedBillToId !== autoBillToId) {
      setAutoBillToId(undefined);
      setHasManualBillToOverride(true);
    }
  }, [watchedBillToId, autoBillToId, setAutoBillToId]);

  return (
    <>
      <div className='w-full [&>div]:min-w-0 [&>div>div:first-child]:!min-w-0 [&>div>div:first-child]:!w-0 [&>div>div:first-child]:!flex-1 [&>div>div:first-child]:!overflow-hidden [&>div>div:last-child]:!shrink-0'>
        <RHFCustomerSelect
          required
          name='customer.externalTMSID'
          label={PLS_SECTION_LABELS.customer}
          control={control}
          errors={errors}
          customers={customers}
          setCustomers={setCustomers}
          isLoadingCustomers={isLoadingCustomers}
          setIsLoadingCustomers={setIsLoadingCustomers}
          tmsID={tmsID}
          placeholder='Search by customer name, email, or domain'
          preserveSearchedSelection
          aiHighlightedExternalTMSID={suggestedCustomerExternalTMSID}
          disabled={!isCreateMode}
        />
      </div>

      {watchedCustomerId && (
        <RHFLocationSelect
          required
          name='plsSelectedLocationId'
          label={PLS_SECTION_LABELS.location}
          control={control}
          errors={errors}
          locations={customerLocationSelectOptions ?? null}
          setLocations={setCustomerLocations}
          isLoadingLocations={isLoadingCustomerLocations}
          handleRefreshLocations={refreshCustomerLocations}
          tmsID={tmsID}
          customerExternalTmsId={watchedCustomerId}
          placeholder='Search location'
          labelIsName
          disabled={!isCreateMode}
          aiHighlightedExternalTMSID={autoLocationId}
          onLocationSelected={handleLocationSelected}
        />
      )}
    </>
  );
}
